import { AnimationPersonalizer } from "@manager/personalizer/AnimationPersonalizer";
import { inject, injectable } from "inversify";
import { PlayerCTAAPI } from "../../../api/cta/PlayerCTAAPI";
import { vintom } from "../../../api/globalVintomNamespace/GlobalVintomNamespace";
import { VintomError } from "../../../api/model/error/VintomError";
import { Config } from "../../../config/config";
import { prepackagedCTAs } from "../../../cta/prepackagedCTAs/prepackagedCTAs";
import { Animation } from "../../../model/playerConfiguration/Animation";
import { FullPlayerConfiguration } from "../../../model/playerConfiguration/FullPlayerConfiguration";
import { ScenarioElement } from "../../../model/playerConfiguration/ScenarioElement";
import { AppliedCTAFunction } from "../../appliedCTAFunction/AppliedCTAFunction";
import { TYPES } from "../../di/types";
import { Personalizer } from "../../personalizer/Personalizer";
import { ResourceLoader } from "../resourceLoader/ResourceLoader";
import { CachedCTAResource } from "./CachedCTAResource";
import { ResourcesCache } from "./ResourcesCache";

export interface ScenarioLoader {
  loadResources(configuration: FullPlayerConfiguration, scenarioEl: ScenarioElement): Promise<ResourcesCache>;
  prepareForRemove(): void;
  setPlayerCTAAPIDelegate(ctaPlayerAPI: PlayerCTAAPI): void;
}

@injectable()
export class ScenarioLoaderImpl implements ScenarioLoader {

  private readonly cache = new ResourcesCache();
  private ctaPlayerAPI: PlayerCTAAPI;

  constructor(
    @inject(TYPES.animationPersonalizer.AnimationPersonalizer) private animationPersonalizer: AnimationPersonalizer,
    @inject(TYPES.personalizer.Personalizer) private personalizer: Personalizer,
    @inject(TYPES.loader.resourceLoader.ResourceLoader) private resourceLoader: ResourceLoader) { }

  setPlayerCTAAPIDelegate(ctaPlayerAPI: PlayerCTAAPI): void {
    this.ctaPlayerAPI = ctaPlayerAPI;
  }

  loadResources(configuration: FullPlayerConfiguration, scenarioEl: ScenarioElement): Promise<ResourcesCache> {
    this.loadPrepackagedCTAResources();
    return Promise.all([
      this.loadAndPersonalizeAnimationResources(scenarioEl, configuration.personalizationJSON),
      this.loadAndPersonalizeCTAResources(scenarioEl, configuration.personalizationJSON, configuration.ctaStorageUrl),
      this.loadPosterResources(scenarioEl)
    ])
      .then(() => Promise.resolve(this.cache));
  }

  prepareForRemove(): void {
    for (const objectUrl of Array.from(this.cache.posterObjectUrlsByUrl.values())) {
      URL.revokeObjectURL(objectUrl);
    }
  }

  private loadPrepackagedCTAResources() {
    const prepackagedCTATypes = Array.from(prepackagedCTAs.keys());
    prepackagedCTATypes.forEach((type) => {
      const ctaResource = prepackagedCTAs.get(type);
      const appliedCTAFunction: AppliedCTAFunction = (ctaId: number, ctaBoard: HTMLElement, ctaCustomAttributes: any) =>
        ctaResource.ctaFunction(this.ctaPlayerAPI, ctaId, ctaBoard, ctaCustomAttributes);
      this.cache.ctasByType.set(type, new CachedCTAResource(appliedCTAFunction, ctaResource.html));
    });
  }

  private loadAndPersonalizeAnimationResources(scenarioEl: ScenarioElement, personalizationJSON: any) {
    return Promise.all(scenarioEl.animations.map((animation) => this.loadAndPersonalizeAnimationResource(animation, personalizationJSON)))
      .then(() => Promise.resolve());
  }

  private loadAndPersonalizeAnimationResource(animation: Animation, personalizationJSON: any): Promise<void> {

    return fetch(animation.src)
        .then((response) => response.json())
        .catch(() => Promise.reject(new VintomError("resourceLoadingFailure", `Failed to load animation ${animation.src}`)))
        .then((json) => {
          if (animation.customAttributes) {
            const customAttributes = { ...animation.customAttributes };
            const values = Object.keys(customAttributes).map((key) => {
              return JSON.parse(JSON.stringify(customAttributes[key]));
            });
            const promises = values
                .reduce((acc, val) => [...acc, ...val], [])
                .filter((valItem) => valItem.type === "image" && valItem.src)
                // tslint:disable-next-line:no-eval
                .map((valItem) => fetch(eval(valItem.src))
                    .then((response) => response.blob()));
            return Promise.all(promises).then(() => json);
          }
        })
        .then((json) => {
          const personalized = this.animationPersonalizer.personalizeAnim(json, animation.customAttributes, personalizationJSON);
          this.cache.animationsBySrc.set(animation.src, personalized);
        });
  }

  private loadPosterResources(scenarioEl: ScenarioElement): Promise<void> {
    const posters = scenarioEl.ctas.map((cta) => cta.poster).filter((poster) => !!poster);
    if (scenarioEl.poster) {
      posters.push(scenarioEl.poster);
    }

    return Promise.all(posters.map((poster) => this.loadImgResource(poster)))
      .then(() => Promise.resolve());
  }

  private loadImgResource(url: string): Promise<void> {
    if (this.cache.posterObjectUrlsByUrl.get(url)) {
      return Promise.resolve();
    }
    return fetch(url)
      .then((response) => response.blob())
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        this.cache.posterObjectUrlsByUrl.set(url, objectUrl);
      })
      .catch(() => console.log("Poster not loaded"));
  }

  private loadAndPersonalizeCTAResources(scenarioEl: ScenarioElement, personalizationJSON: any, ctaStorageUrl: string) {
    const types = scenarioEl.ctas.map((cta) => cta.type);

    scenarioEl.ctas.forEach((cta) => {
      if (cta.customAttributes) {
        cta.customAttributes = this.personalizer.personalize(cta.customAttributes, personalizationJSON);
      }
    });

    return Promise.all(
      types.map((type) => {

        const htmlUrl = Config.htmlCTAStorageUrlPattern
          .replace(Config.ctaStorageUrlPlaceholder, ctaStorageUrl)
          .replace(Config.ctaTypePlaceholder, type);
        const jsUrl = Config.jsCTAStorageUrlPattern
          .replace(Config.ctaStorageUrlPlaceholder, ctaStorageUrl)
          .replace(Config.ctaTypePlaceholder, type);
        const cssUrl = Config.cssCTAStorageUrlPattern
            .replace(Config.ctaStorageUrlPlaceholder, ctaStorageUrl)
            .replace(Config.ctaTypePlaceholder, type);

        return this.loadCTAResource(type, htmlUrl, jsUrl, cssUrl);
      }))
      .then(() => Promise.resolve());
  }

  private loadCTAResource(type: string, htmlSrc: string, jsSrc: string, cssSrc: string): Promise<void> {
    if (this.cache.ctasByType.get(type)) {
      return Promise.resolve();
    }

    const loadedHtml = fetch(htmlSrc)
      .then((response) => response.text());

    const jsLoadedSuccessfully = this.resourceLoader.loadResources([{ location: jsSrc, type: "JS" }]);
    const cssLoadedSuccessfully = this.resourceLoader.loadResources([{ location: cssSrc, type: "CSS" }], true);
    console.log("load-" + type);
    return Promise.all([loadedHtml, jsLoadedSuccessfully, cssLoadedSuccessfully])
      .catch(() => Promise.reject(new VintomError("resourceLoadingFailure", `Failed to load cta for type ${type}`)))
      .then(([html]) => {
        console.log("laoded-" + type);
        const ctaFunction = vintom.cta[type];
        // if (!ctaFunction) {
        //   return Promise.reject(new VintomError("resourceLoadingFailure", `Missing CTA function in cta.js file.
        //     Please make sure to add cta function to vintom.cta dictionary (vintom.cta.${type} = function(....) { .... }))`));
        // }
        const appliedCTAFuntion: AppliedCTAFunction = (ctaId: number, ctaBoard: HTMLElement, ctaCustomAttributes: any) =>
          ctaFunction(this.ctaPlayerAPI, ctaId, ctaBoard, ctaCustomAttributes);

        this.cache.ctasByType.set(type, new CachedCTAResource(appliedCTAFuntion, html));
      });
  }

}
